import { router } from "@inertiajs/vue3";
import { onBeforeUnmount, onMounted, ref } from "vue";

import { isThreadListRealtimeEvent } from "@/lib/discussion-thread-list";
import { useCableSubscription } from "@/lib/use-cable";
import type { RealtimeDiscussionEvent } from "@/types";

export interface UseDiscussionThreadListRealtimeOptions {
  onThreadListChange?: () => void;
  onRealtimeEvent?: (message: RealtimeDiscussionEvent) => void;
}

export function useDiscussionThreadListRealtime(
  options: UseDiscussionThreadListRealtimeOptions = {},
) {
  const threadListReloadInProgress = ref(false);
  const pendingReload = ref(false);
  const lastResyncAt = ref(0);
  const resyncThrottleMs = 3_000;

  const reloadThreadLists = () => {
    if (threadListReloadInProgress.value) {
      pendingReload.value = true;
      return;
    }

    threadListReloadInProgress.value = true;
    router.reload({
      async: true,
      only: ["threads_active", "threads_archived"],
      onFinish: () => {
        threadListReloadInProgress.value = false;
        if (pendingReload.value) {
          pendingReload.value = false;
          reloadThreadLists();
        }
      },
    });
  };

  const resyncThreadLists = () => {
    const now = Date.now();
    if (now - lastResyncAt.value < resyncThrottleMs) return;

    lastResyncAt.value = now;
    reloadThreadLists();
    options.onThreadListChange?.();
  };

  useCableSubscription<RealtimeDiscussionEvent>(
    { channel: "DiscussionMessagesChannel" },
    (message) => {
      if (!isThreadListRealtimeEvent(message)) return;

      options.onRealtimeEvent?.(message);
      reloadThreadLists();
      options.onThreadListChange?.();
    },
  );

  const handleWindowFocus = () => {
    resyncThreadLists();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== "visible") return;
    resyncThreadLists();
  };

  const handleOnline = () => {
    resyncThreadLists();
  };

  onMounted(() => {
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("focus", handleWindowFocus);
    window.removeEventListener("online", handleOnline);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  });

  return {
    threadListReloadInProgress,
    pendingReload,
    reloadThreadLists,
    resyncThreadLists,
  };
}
